import { useEffect, useState } from 'react';
import api from '../../api/axios';
import S3ImagePicker from '../components/S3ImagePicker';

const TABS = ['Vision & Mission', 'Objectives', 'Leadership', 'Executive Members', 'Advisory Board'];

const EMPTY_LEADER  = { name: '', position: '', photo: '' };
const EMPTY_ADVISOR = { name: '', designation: '', photo: '' };

// ── Shared image input with browse button ──────────────────────────────────
function PhotoInput({ value, onChange }) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="/assets/img/team/name.jpg or S3 URL"
        />
        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPicker(true)}>
          <i className="fas fa-images me-1"></i>Browse
        </button>
      </div>
      {value && (
        <img
          src={value}
          alt="preview"
          style={{ height: 56, width: 56, borderRadius: '50%', objectFit: 'cover', marginTop: 6, border: '2px solid #dee2e6' }}
        />
      )}
      {showPicker && (
        <S3ImagePicker
          onSelect={url => { onChange(url); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}

// ── Member table ───────────────────────────────────────────────────────────
function MemberTable({ members, onEdit, onDelete, positionLabel = 'Position' }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover mb-0">
        <thead className="table-light">
          <tr><th style={{ width: 56 }}>Photo</th><th>Name</th><th>{positionLabel}</th><th style={{ width: 120 }}></th></tr>
        </thead>
        <tbody>
          {members.length === 0 && (
            <tr><td colSpan="4" className="text-center text-muted py-4">No members yet.</td></tr>
          )}
          {members.map((m, i) => (
            <tr key={i}>
              <td className="align-middle">
                {m.photo
                  ? <img src={m.photo} alt={m.name} style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '1px solid #dee2e6' }} />
                  : <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-user text-muted" style={{ fontSize: 14 }}></i>
                    </div>
                }
              </td>
              <td className="align-middle fw-medium">{m.name}</td>
              <td className="align-middle text-muted small">{m.position || m.designation}</td>
              <td className="align-middle">
                <button className="btn btn-sm btn-outline-primary me-1" onClick={() => onEdit(i)}>Edit</button>
                <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(i)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Add / Edit member form ─────────────────────────────────────────────────
function MemberForm({ value, onChange, onSave, onCancel, saving, positionLabel = 'Position' }) {
  const isAdvisor = value.designation !== undefined;
  const posField  = isAdvisor ? 'designation' : 'position';
  const set = (k, v) => onChange({ ...value, [k]: v });

  return (
    <div className="card border-0 shadow-sm mb-3 border-start border-primary border-3">
      <div className="card-body p-4">
        <h6 className="fw-bold mb-3">{value.name ? `Editing: ${value.name}` : 'New Member'}</h6>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small fw-bold">Name *</label>
            <input className="form-control" value={value.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-bold">{positionLabel} *</label>
            <input className="form-control" value={value[posField] || ''} onChange={e => set(posField, e.target.value)} placeholder={positionLabel} />
          </div>
          <div className="col-12">
            <label className="form-label small fw-bold">Photo</label>
            <PhotoInput value={value.photo} onChange={v => set('photo', v)} />
          </div>
        </div>
        <div className="d-flex gap-2 mt-3">
          <button className="btn btn-primary rounded-pill px-4" onClick={onSave} disabled={saving || !value.name.trim()}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : 'Save Member'}
          </button>
          <button className="btn btn-outline-secondary rounded-pill px-4" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function AboutManager() {
  const [tab, setTab]         = useState(0);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editIdx, setEditIdx]   = useState(null);
  const [formVal, setFormVal]   = useState(EMPTY_LEADER);

  useEffect(() => {
    api.get('/content/aboutUs')
      .then(r => setData(r.data || {}))
      .catch(() => setError('Failed to load about content'))
      .finally(() => setLoading(false));
  }, []);

  const persist = async (updated) => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.put('/content/aboutUs', updated);
      setData(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Objectives ────────────────────────────────────────────────────────
  const addObjective    = () => persist({ ...data, objectives: [...(data.objectives || []), ''] });
  const updateObj       = (i, v) => { const o = [...(data.objectives || [])]; o[i] = v; setData(d => ({ ...d, objectives: o })); };
  const removeObjective = (i) => persist({ ...data, objectives: (data.objectives || []).filter((_, idx) => idx !== i) });

  // ── Member list helpers ───────────────────────────────────────────────
  const getList = () => {
    if (tab === 2) return data?.executiveCommittee?.leadership || [];
    if (tab === 3) return data?.executiveCommittee?.members    || [];
    if (tab === 4) return data?.advisoryBoard?.members         || [];
    return [];
  };

  const buildUpdated = (list) => {
    if (tab === 2) return { ...data, executiveCommittee: { ...(data.executiveCommittee || {}), leadership: list } };
    if (tab === 3) return { ...data, executiveCommittee: { ...(data.executiveCommittee || {}), members:    list } };
    if (tab === 4) return { ...data, advisoryBoard:      { ...(data.advisoryBoard      || {}), members:    list } };
    return data;
  };

  const openAdd = () => {
    setEditIdx(null);
    setFormVal(tab === 4 ? { ...EMPTY_ADVISOR } : { ...EMPTY_LEADER });
    setShowForm(true);
  };

  const openEdit = (i) => {
    setEditIdx(i);
    setFormVal({ ...getList()[i] });
    setShowForm(true);
  };

  const saveMember = async () => {
    const list = [...getList()];
    if (editIdx !== null) list[editIdx] = formVal;
    else list.push(formVal);
    await persist(buildUpdated(list));
    setShowForm(false);
  };

  const deleteMember = async (i) => {
    if (!window.confirm('Delete this member?')) return;
    await persist(buildUpdated(getList().filter((_, idx) => idx !== i)));
  };

  // ── Switch tab ────────────────────────────────────────────────────────
  const switchTab = (i) => { setTab(i); setShowForm(false); setEditIdx(null); };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  if (!data)   return <div className="alert alert-danger">Could not load about content.</div>;

  const posLabel = tab === 4 ? 'Designation' : 'Position';

  return (
    <div>
      <div className="mb-4">
        <h4 className="fw-bold mb-0">About &amp; Team</h4>
        <p className="text-muted small mb-0">Edit vision, mission, objectives, and team members</p>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {saved  && <div className="alert alert-success py-2"><i className="fas fa-check-circle me-2"></i>Saved successfully.</div>}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        {TABS.map((t, i) => (
          <li key={t} className="nav-item">
            <button className={`nav-link ${tab === i ? 'active' : ''}`} onClick={() => switchTab(i)}>{t}</button>
          </li>
        ))}
      </ul>

      {/* ── Vision & Mission ── */}
      {tab === 0 && (
        <form onSubmit={e => { e.preventDefault(); persist(data); }}>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-label small fw-bold">Vision</label>
                  <textarea rows="4" className="form-control"
                    value={data.vision?.content || ''}
                    onChange={e => setData(d => ({ ...d, vision: { ...(d.vision || {}), content: e.target.value } }))}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Mission</label>
                  <textarea rows="4" className="form-control"
                    value={data.mission?.content || ''}
                    onChange={e => setData(d => ({ ...d, mission: { ...(d.mission || {}), content: e.target.value } }))}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-5">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}

      {/* ── Objectives ── */}
      {tab === 1 && (
        <form onSubmit={e => { e.preventDefault(); persist(data); }}>
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body p-4">
              <div className="d-flex flex-column gap-2">
                {(data.objectives || []).map((obj, i) => (
                  <div key={i} className="d-flex gap-2 align-items-center">
                    <span className="text-muted small fw-bold" style={{ minWidth: 22 }}>{i + 1}.</span>
                    <input className="form-control form-control-sm" value={obj}
                      onChange={e => updateObj(i, e.target.value)} />
                    <button type="button" className="btn btn-sm btn-outline-danger px-2" onClick={() => removeObjective(i)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-sm btn-outline-primary rounded-pill px-3 mt-3" onClick={addObjective}>
                <i className="fas fa-plus me-1"></i>Add Objective
              </button>
            </div>
          </div>
          <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-5">
            {saving ? 'Saving…' : 'Save Objectives'}
          </button>
        </form>
      )}

      {/* ── Leadership / Executive / Advisory ── */}
      {(tab === 2 || tab === 3 || tab === 4) && (
        <div>
          {/* Section title for leadership */}
          {tab === 2 && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-3 d-flex gap-3 align-items-center">
                <div className="flex-grow-1">
                  <label className="form-label small fw-bold mb-1">Section Title</label>
                  <input className="form-control form-control-sm"
                    value={data.executiveCommittee?.title || ''}
                    onChange={e => setData(d => ({ ...d, executiveCommittee: { ...(d.executiveCommittee || {}), title: e.target.value } }))}
                  />
                </div>
                <div className="align-self-end">
                  <button className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => persist(data)}>Save Title</button>
                </div>
              </div>
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small">{getList().length} member{getList().length !== 1 ? 's' : ''}</span>
            <button className="btn btn-primary rounded-pill px-4" onClick={openAdd}>
              <i className="fas fa-plus me-2"></i>Add Member
            </button>
          </div>

          {showForm && (
            <MemberForm
              value={formVal}
              onChange={setFormVal}
              onSave={saveMember}
              onCancel={() => setShowForm(false)}
              saving={saving}
              positionLabel={posLabel}
            />
          )}

          <div className="card border-0 shadow-sm">
            <MemberTable
              members={getList()}
              onEdit={openEdit}
              onDelete={deleteMember}
              positionLabel={posLabel}
            />
          </div>
        </div>
      )}
    </div>
  );
}
